import { from, of, concat } from 'rxjs';
import assert from '@banking/ui/utils/assert';
import workflow from '@banking/ui/lib/workflow';
import { StartCardActivationRequest, TwoFactorAuthenticationRequestType, VerifyTwoFactorAuthenticationTokenRequest, VerifyTwoFactorAuthenticationTokenResponse, FinishCardActivationRequest, FinishCardActivationResponse, SetPasscodeRequest, SetPasscodeResponse } from '@square/dashboard-protos/models/squareup/bizbank/service';

// State
// =============================================================================

export let Screen = /*#__PURE__*/function (Screen) {
  Screen[Screen["None"] = 0] = "None";
  Screen[Screen["EMAIL"] = 1] = "EMAIL";
  Screen[Screen["DETAILS"] = 2] = "DETAILS";
  Screen[Screen["CREATE_PIN"] = 3] = "CREATE_PIN";
  Screen[Screen["SUCCESS"] = 4] = "SUCCESS";
  return Screen;
}({});
export const INITIAL_STATE = {
  card: null,
  cardToken: '',
  confirmPinValue: '',
  confirmPinErrorMessage: '',
  createPinValue: '',
  createPinErrorMessage: '',
  createPinScreenErrorMessage: '',
  cvcValue: '',
  cvcErrorMessage: '',
  detailsErrorMessage: '',
  emailConfirmationCode: '',
  emailConfirmationErrorMessage: '',
  expiryDateValue: '',
  expiryDateErrorMessage: '',
  hasPendingActivation: false,
  isLoading: false,
  isOpen: false,
  screen: Screen.None,
  unitToken: ''
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["FinishCardActivationReceived"] = "FINISH_CARD_ACTIVATION_RECEIVED";
  ActionType["FinishCardActivationRejected"] = "FINISH_CARD_ACTIVATION_REJECTED";
  ActionType["IncrementScreen"] = "INCREMENT_SCREEN";
  ActionType["StartCardActivationReceived"] = "START_CARD_ACTIVATION_RECEIVED";
  ActionType["SetPasscodeReceived"] = "SET_PASSCODE_RECEIVED";
  ActionType["SetPasscodeRejected"] = "SET_PASSCODE_REJECTED";
  ActionType["ToggleLoading"] = "TOGGLE_LOADING";
  ActionType["ToggleModal"] = "TOGGLE_MODAL";
  ActionType["ToggleModalByCardToken"] = "TOGGLE_MODAL_BY_CARD_TOKEN";
  ActionType["UserChangeConfirmPin"] = "USER_CHANGE_CONFIRM_PIN";
  ActionType["UserChangeCreatePin"] = "USER_CHANGE_CREATE_PIN";
  ActionType["UserChangeCvc"] = "USER_CHANGE_CVC";
  ActionType["UserChangeEmailConfirmationCode"] = "USER_CHANGE_EMAIL_CONFIRMATION_CODE";
  ActionType["UserChangeExpiryDate"] = "USER_CHANGE_EXPIRY_DATE";
  ActionType["UserClickResendActivationToken"] = "USER_CLICK_RESEND_ACTIVATION_TOKEN";
  ActionType["UserClickSetPasscode"] = "USER_CLICK_SET_PASSCODE";
  ActionType["UserClickVerifyActivationToken"] = "USER_CLICK_VERIFY_ACTIVATION_TOKEN";
  ActionType["UserClickVerifyCardDetails"] = "USER_CLICK_VERIFY_CARD_DETAILS";
  ActionType["VerifyTwoFactorAuthenticationTokenReceived"] = "VERIFY_TWO_FACTOR_AUTHENTICATION_TOKEN_RECEIVED";
  ActionType["VerifyTwoFactorAuthenticationTokenRejected"] = "VERIFY_TWO_FACTOR_AUTHENTICATION_TOKEN_REJECTED";
  return ActionType;
}({});
// Action Creators
// =============================================================================

export const finishCardActivationReceivedAction = response => ({
  type: ActionType.FinishCardActivationReceived,
  response
});
export const finishCardActivationRejectedAction = error => ({
  type: ActionType.FinishCardActivationRejected,
  error
});
export const incrementScreenAction = screen => ({
  type: ActionType.IncrementScreen,
  screen
});
export const startCardActivationReceivedAction = response => ({
  type: ActionType.StartCardActivationReceived,
  response
});
export const setPasscodeReceivedAction = response => ({
  type: ActionType.SetPasscodeReceived,
  response
});
export const setPasscodeRejectedAction = error => ({
  type: ActionType.SetPasscodeRejected,
  error
});
export const toggleLoadingAction = value => ({
  type: ActionType.ToggleLoading,
  value
});
export const toggleModalAction = (isOpen, unitToken, card) => ({
  type: ActionType.ToggleModal,
  isOpen,
  unitToken,
  card
});
export const toggleModalByCardTokenAction = (isOpen, unitToken, cardToken) => ({
  type: ActionType.ToggleModalByCardToken,
  isOpen,
  unitToken,
  cardToken
});
export const userChangeConfirmPinAction = value => ({
  type: ActionType.UserChangeConfirmPin,
  value
});
export const userChangeCreatePinAction = value => ({
  type: ActionType.UserChangeCreatePin,
  value
});
export const userChangeCvcAction = value => ({
  type: ActionType.UserChangeCvc,
  value
});
export const userChangeEmailConfirmationCodeAction = value => ({
  type: ActionType.UserChangeEmailConfirmationCode,
  value
});
export const userChangeExpiryDateAction = value => ({
  type: ActionType.UserChangeExpiryDate,
  value
});
export const userClickResendActivationTokenAction = () => ({
  type: ActionType.UserClickResendActivationToken
});
export const userClickSetPasscodeAction = () => ({
  type: ActionType.UserClickSetPasscode
});
export const userClickVerifyActivationTokenAction = () => ({
  type: ActionType.UserClickVerifyActivationToken
});
export const userClickVerifyCardDetailsAction = () => ({
  type: ActionType.UserClickVerifyCardDetails
});
export const verifyTwoFactorAuthenticationTokenReceivedAction = response => ({
  type: ActionType.VerifyTwoFactorAuthenticationTokenReceived,
  response
});
export const verifyTwoFactorAuthenticationTokenRejectedAction = error => ({
  type: ActionType.VerifyTwoFactorAuthenticationTokenRejected,
  error
});

// Update
// =============================================================================

function handleFinishCardActivationReceived(context, state, action) {
  const {
    result,
    pinIsSet
  } = action.response;
  const {
    Result
  } = FinishCardActivationResponse;
  switch (result) {
    case Result.SUCCESS:
      return [{
        ...state,
        detailsErrorMessage: ''
      }, () => incrementScreenAction(pinIsSet ? Screen.SUCCESS : Screen.CREATE_PIN)];
    case Result.INCORRECT_CVV:
    case Result.INCORRECT_EXPIRATION:
    case Result.INCORRECT_CARD_INFO:
      {
        const detailsErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.details.error.invalidData');
        return [{
          ...state,
          detailsErrorMessage
        }, null];
      }
    case Result.ACTIVATION_TOKEN_INVALID:
    case Result.ACTIVATION_TOKEN_EXPIRED:
    case Result.FAILED:
      {
        const detailsErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
        context.sentry.captureException(`FinishCardActivationResponse.Result: ${result}`);
        return [{
          ...state,
          detailsErrorMessage
        }, null];
      }
  }
}
function handleFinishCardActivationRejected(context, state, action) {
  const detailsErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
  context.sentry.captureException(action.error);
  return [{
    ...state,
    detailsErrorMessage
  }, null];
}
function handleIncrementScreen(state, action) {
  const {
    screen
  } = action;
  return [{
    ...state,
    screen
  }, null];
}
function handleSetPasscodeReceived(context, state, action) {
  const {
    result
  } = action.response;
  const {
    Result
  } = SetPasscodeResponse;
  switch (result) {
    case Result.SUCCESS:
      return [{
        ...state,
        createPinScreenErrorMessage: ''
      }, () => incrementScreenAction(Screen.SUCCESS)];
    case Result.FAILED_WEAK_PASSCODE:
      {
        const createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.weak');
        return [{
          ...state,
          createPinScreenErrorMessage
        }, null];
      }
    case Result.INVALID_CARD_TOKEN:
    case Result.AUTHENTICATION_TOKEN_INVALID:
    case Result.AUTHENTICATION_TOKEN_EXPIRED:
    case Result.FAILED:
      {
        const createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
        context.sentry.captureException(`SetPasscodeResponse.Result: ${result}`);
        return [{
          ...state,
          createPinScreenErrorMessage
        }, null];
      }
  }
}
function handleSetPasscodeRejected(context, state, action) {
  const createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
  context.sentry.captureException(action.error);
  return [{
    ...state,
    createPinScreenErrorMessage
  }, null];
}
function handleStartCardActivationReceived(context, state, action) {
  const work = () => {
    const {
      success
    } = action.response;
    if (success) {
      context.marketToasts.success(context.intl.t('banking.checking.cardSettings.cardActivation.email.resendConfirmationToast.message'));
    } else {
      context.marketToasts.critical(context.intl.t('banking.checking.cardSettings.cardActivation.error.server'));
    }
    return null;
  };
  return [state, work];
}
function handleToggleLoading(state, action) {
  const {
    value
  } = action;
  const isLoading = value ?? !state.isLoading;
  return [{
    ...state,
    isLoading
  }, null];
}
function handleToggleModal(context, state, action) {
  const {
    isWithout2FAEnabled,
    bizbank
  } = context;
  const {
    isOpen,
    unitToken,
    card
  } = action;
  if (isOpen) {
    assert(unitToken);
    assert(card);
    const screen = isWithout2FAEnabled ? Screen.DETAILS : Screen.EMAIL;
    const work = () => {
      // send email activation token one was not already send.
      if (!isWithout2FAEnabled && card.active2faTokenTypes.includes(TwoFactorAuthenticationRequestType.CARD_ACTIVATION)) {
        // Response can be ignored. Seller can click resend email link.
        bizbank.startCardActivation(StartCardActivationRequest.create({
          unitToken,
          cardToken: card.cardToken
        }));
      }
      return null;
    };
    return [{
      ...state,
      screen,
      isOpen,
      unitToken,
      card
    }, work];
  }

  // reset state on modal close
  return [INITIAL_STATE, null];
}
function handleToggleModalByCardToken(context, state, action) {
  const {
    isWithout2FAEnabled,
    bizbank
  } = context;
  const {
    isOpen,
    unitToken,
    cardToken
  } = action;
  if (isOpen) {
    assert(unitToken && unitToken.length > 0);
    assert(cardToken && cardToken.length > 0);
    const screen = isWithout2FAEnabled ? Screen.DETAILS : Screen.EMAIL;
    const work = () => {
      if (!isWithout2FAEnabled) {
        // Response can be ignored. Seller can click resend email link.
        bizbank.startCardActivation(StartCardActivationRequest.create({
          unitToken,
          cardToken
        }));
      }
      return null;
    };
    return [{
      ...state,
      screen,
      isOpen,
      unitToken,
      cardToken
    }, work];
  }
  // reset state on modal close
  return [INITIAL_STATE, null];
}
function handleUserChangeConfirmPin(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    confirmPinValue: value,
    confirmPinErrorMessage: '',
    createPinScreenErrorMessage: ''
  }, null];
}
function handleUserChangeCreatePin(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    createPinValue: value,
    createPinErrorMessage: '',
    createPinScreenErrorMessage: ''
  }, null];
}
function handleUserChangeCvc(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    cvcValue: value,
    cvcErrorMessage: '',
    detailsErrorMessage: ''
  }, null];
}
function handleUserChangeEmailConfirmationCode(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    emailConfirmationCode: value,
    emailConfirmationErrorMessage: ''
  }, null];
}
function handleUserChangeExpiryDate(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    expiryDateValue: value,
    expiryDateErrorMessage: '',
    detailsErrorMessage: ''
  }, null];
}
function handleUserClickResendActivationToken(context, state) {
  assert(state.unitToken);
  assert(state.card);
  const work = () => {
    const response = context.bizbank.startCardActivation(StartCardActivationRequest.create({
      unitToken: state.unitToken,
      cardToken: state.card?.cardToken
    })).then(startCardActivationReceivedAction);
    return from(response);
  };
  return [state, work];
}
function handleUserClickSetPasscode(context, state) {
  let createPinErrorMessage = '';
  let confirmPinErrorMessage = '';
  let createPinScreenErrorMessage = '';
  if (state.createPinValue.length === 0) {
    createPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.create');
  } else if (state.createPinValue.length < 4) {
    createPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.validInput');
  }
  if (state.confirmPinValue.length === 0) {
    confirmPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.confirm');
  } else if (state.confirmPinValue.length < 4) {
    confirmPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.validInput');
  }
  if (state.createPinValue !== state.confirmPinValue) {
    createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.notMatch');
  }
  if (createPinErrorMessage || confirmPinErrorMessage || createPinScreenErrorMessage) {
    return [{
      ...state,
      createPinErrorMessage,
      confirmPinErrorMessage,
      createPinScreenErrorMessage
    }, null];
  }
  assert(state.card?.cardToken || state.cardToken, 'Missing card token');
  const work = () => {
    const setPasscodeResponse = context.bizbank.setPasscode(SetPasscodeRequest.create({
      passcode: state.createPinValue,
      cardToken: state.card?.cardToken || state.cardToken
    })).then(setPasscodeReceivedAction, setPasscodeRejectedAction);
    return concat(of(toggleLoadingAction(true)), from(setPasscodeResponse), of(toggleLoadingAction(false)));
  };
  return [state, work];
}
function handleUserClickVerifyActivationToken(context, state) {
  if (state.emailConfirmationCode.length === 0) {
    const emailConfirmationErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.email.error.empty');
    return [{
      ...state,
      emailConfirmationErrorMessage
    }, null];
  }
  const work = () => {
    const verifyTwoFactorAuthenticationTokenResponse = context.bizbank.verifyTwoFactorAuthenticationToken(VerifyTwoFactorAuthenticationTokenRequest.create({
      authenticationToken: state.emailConfirmationCode,
      requestType: TwoFactorAuthenticationRequestType.CARD_ACTIVATION
    })).then(verifyTwoFactorAuthenticationTokenReceivedAction, verifyTwoFactorAuthenticationTokenRejectedAction);
    return concat(of(toggleLoadingAction(true)), from(verifyTwoFactorAuthenticationTokenResponse), of(toggleLoadingAction(false)));
  };
  return [state, work];
}
function handleUserClickVerifyCardDetails(context, state) {
  let expiryDateErrorMessage = '';
  let cvcErrorMessage = '';
  if (state.expiryDateValue.length === 0) {
    expiryDateErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.details.expiryDate.error.empty');
  }
  if (state.cvcValue.length === 0) {
    cvcErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.details.cvc.error.empty');
  }
  if (expiryDateErrorMessage || cvcErrorMessage) {
    return [{
      ...state,
      expiryDateErrorMessage,
      cvcErrorMessage
    }, null];
  }
  const work = () => {
    const request = context.isWithout2FAEnabled ? FinishCardActivationRequest.create({
      cardToken: state.card?.cardToken || state.cardToken,
      cvv: state.cvcValue,
      expirationMmyy: state.expiryDateValue,
      unitToken: state.unitToken
    }) : FinishCardActivationRequest.create({
      activationToken: state.emailConfirmationCode,
      cvv: state.cvcValue,
      expirationMmyy: state.expiryDateValue
    });
    const finishCardActivationResponse = context.bizbank.finishCardActivation(request).then(finishCardActivationReceivedAction, finishCardActivationRejectedAction);
    return concat(of(toggleLoadingAction(true)), from(finishCardActivationResponse), of(toggleLoadingAction(false)));
  };
  return [state, work];
}
function handleVerifyTwoFactorAuthenticationTokenReceived(context, state, action) {
  const {
    result
  } = action.response;
  const {
    Result
  } = VerifyTwoFactorAuthenticationTokenResponse;
  switch (result) {
    case Result.VERIFIED:
      return [state, () => incrementScreenAction(Screen.DETAILS)];
    case Result.INVALID_TOKEN:
      {
        const emailConfirmationErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.email.error.invalid');
        return [{
          ...state,
          emailConfirmationErrorMessage
        }, null];
      }
    case Result.EXPIRED_TOKEN:
      {
        const emailConfirmationErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.email.error.expired');
        return [{
          ...state,
          emailConfirmationErrorMessage
        }, null];
      }
  }
}
function handleVerifyTwoFactorAuthenticationTokenRejected(context, state, action) {
  const emailConfirmationErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
  context.sentry.captureException(action.error);
  return [{
    ...state,
    emailConfirmationErrorMessage
  }, null];
}
export const update = context =>
// eslint-disable-next-line complexity
(state, action) => {
  switch (action.type) {
    case ActionType.FinishCardActivationReceived:
      return handleFinishCardActivationReceived(context, state, action);
    case ActionType.FinishCardActivationRejected:
      return handleFinishCardActivationRejected(context, state, action);
    case ActionType.IncrementScreen:
      return handleIncrementScreen(state, action);
    case ActionType.SetPasscodeReceived:
      return handleSetPasscodeReceived(context, state, action);
    case ActionType.SetPasscodeRejected:
      return handleSetPasscodeRejected(context, state, action);
    case ActionType.StartCardActivationReceived:
      return handleStartCardActivationReceived(context, state, action);
    case ActionType.ToggleLoading:
      return handleToggleLoading(state, action);
    case ActionType.ToggleModal:
      return handleToggleModal(context, state, action);
    case ActionType.ToggleModalByCardToken:
      return handleToggleModalByCardToken(context, state, action);
    case ActionType.UserChangeConfirmPin:
      return handleUserChangeConfirmPin(state, action);
    case ActionType.UserChangeCreatePin:
      return handleUserChangeCreatePin(state, action);
    case ActionType.UserChangeCvc:
      return handleUserChangeCvc(state, action);
    case ActionType.UserChangeEmailConfirmationCode:
      return handleUserChangeEmailConfirmationCode(state, action);
    case ActionType.UserChangeExpiryDate:
      return handleUserChangeExpiryDate(state, action);
    case ActionType.UserClickResendActivationToken:
      return handleUserClickResendActivationToken(context, state);
    case ActionType.UserClickSetPasscode:
      return handleUserClickSetPasscode(context, state);
    case ActionType.UserClickVerifyActivationToken:
      return handleUserClickVerifyActivationToken(context, state);
    case ActionType.UserClickVerifyCardDetails:
      return handleUserClickVerifyCardDetails(context, state);
    case ActionType.VerifyTwoFactorAuthenticationTokenReceived:
      return handleVerifyTwoFactorAuthenticationTokenReceived(context, state, action);
    case ActionType.VerifyTwoFactorAuthenticationTokenRejected:
      return handleVerifyTwoFactorAuthenticationTokenRejected(context, state, action);
  }
};
const debitCardActivation = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default debitCardActivation;